import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, useTheme } from "@mui/material";
import {
  useEffect,
  useMemo,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import BoolSetting from "../../../../../../../components/global/Settings/BoolSetting";
import DropdownSetting from "../../../../../../../components/global/Settings/DropdownSetting";
import SettingsSaveButton from "../../../../../../../components/global/Settings/SettingsSaveButton";
import useManualSettingsSave from "../../../../../../../components/global/Settings/useManualSettingsSave";
import ShLabel from "../../../../../../../components/theme/label/ShLabel";
import Subheading from "../../../../../../../components/theme/text/Subheading";
import Text from "../../../../../../../components/theme/text/Text";
import { useGetEnumsQuery, useGetSettingsQuery } from "../../../../../../../newapi/global/generalApiSlice";
import { tokens } from "../../../../../../../theme";


const PickBasis = (props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  

  useEffect(() => {
    props.replaceView({
        ...props.config,
    },);

    return () => {};
  }, []);

  const { data: settings} = useGetSettingsQuery("picking");
  const { data: pickingProcessTypeEnums, enumIsLoading } = useGetEnumsQuery("pickingprocesstype");
  const { data: pickingProductProcessTypeEnums, enumIsLoading: productEnumIsLoading } = useGetEnumsQuery("pickingproductprocesstype");
  const { handleChange, hasChanges, isSaving, save } = useManualSettingsSave();

  // These two settings gate each other, so the gating follows what is picked on screen
  // rather than what is stored: nothing here is written before Save.
  const [withoutCartScan, setWithoutCartScan] = useState(false);
  const [defaultProcessType, setDefaultProcessType] = useState();

  useEffect(() => {
    if (settings) {
      setWithoutCartScan(settings.find(setting => setting.key === "picking_without_cart_scan")?.value === "true");
      setDefaultProcessType(settings.find(setting => setting.key === "picking_default_process_type")?.value);
    }
  }, [settings]);

  const pickingProcessTypes = useMemo(() => {
    if (!pickingProcessTypeEnums) {
      return [];
    }
    const filterTypes = ["auto"];
    if (withoutCartScan) {
      filterTypes.push("cart_location","cart_location_crate");
    }

    return pickingProcessTypeEnums["pickingprocesstype"].filter(item => !filterTypes.includes(item)).map((item, index) => ({ id: item, value: item, name: t(item) }));
  }, [pickingProcessTypeEnums, withoutCartScan, t]);

  const disableWithoutCartScan = ["cart_location","cart_location_crate"].includes(defaultProcessType);

  return  (
    <Box
      sx={{
        height: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: 1,
          gap: 4,
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? colors.grey[300]
              : colors.bg["tertiary"],
          paddingX: 2,
          paddingTop: 4,
          height: 1,
          position: "relative",
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          "@media screen and (max-width: 47.9375em)": {
            paddingTop: 0,
            paddingX: 0,
            background: colors.bg["tertiary"],
          },
          "& .carrier": {
            paddingLeft: 0,
            marginRight: 2,
          },
          "& [aria-label='Carrier']": {
            // background:"purple",
            paddingLeft: 0,
            marginRight: 2,
          },
        }}
      >
        <Box
          sx={{
            paddingX: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* The heading is the part that gives way on a phone; the save action has to stay
              reachable there, so the breakpoint sits on the text rather than on the row. */}
          <Box
            sx={{
              "@media screen and (max-width: 47.9375em)": {
                display: "none",
              },
            }}
          >
            <Subheading>{t("core_pickbasis_heading")}</Subheading>
            <Text light>{t("core_pickbasis_subtitle")}</Text>
            <Box>
              <Box sx={{ display: "inline-block" }}>
                <ShLabel size={32} fitted variant="green">
                  {t("core_pickbasis_label1")}
                </ShLabel>
              </Box>
              <Box sx={{ display: "inline-block" }}>
                <ShLabel size={32} fitted variant="red">
                  {t("core_pickbasis_label2")}
                </ShLabel>
              </Box>
            </Box>
          </Box>
          <SettingsSaveButton onClick={save} isLoading={isSaving} disabled={!hasChanges} />
        </Box>
        {/* <GridActions></GridActions> */}
        <Box
          sx={{
            background:
              theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
            flex: 1,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            "@media screen and (max-width: 47.9375em)": {
              // display:"none"
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[200]
                  : colors.grey[0],
            },
          }}
        >
          <Box
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[400]
                  : colors.grey[0],
              padding: 3,
              display:"flex",
              flexDirection:"column",
              gap:2,
              "@media screen and (max-width: 47.9375em)": {
                // display:"none"
                background:
                  theme.palette.mode === "dark"
                    ? colors.grey[200]
                    : colors.grey[0],
                    gap:2,
              },
            }}
          >
            <DropdownSetting 
              manualSave
              onChange={(value, setting) => {
                handleChange(value, setting);
                setDefaultProcessType(value);
              }}
              settings={settings} 
              objects={pickingProcessTypes ?? []} 
              noselection={"choose_type"}
              variant={"default"} 
              skey="picking_default_process_type"
              explainer={true}
              explainerTitle={t("core_pickbasis_default_process_type_title")}
              explainerText={t("core_pickbasis_default_process_type_text")}
            />
            <DropdownSetting 
              manualSave
              onChange={handleChange}
              settings={settings} 
              objects={pickingProductProcessTypeEnums ? pickingProductProcessTypeEnums["pickingproductprocesstype"]?.map((item, index) => ({ id: item, value: item, name: t(item) })) : []} 
              noselection={"choose_type"}
              variant={"default"} 
              skey="picking_product_process_type"
            />
            <BoolSetting 
            manualSave
            onChange={(value, setting) => {
              handleChange(value, setting);
              setWithoutCartScan(value === true || value === "true");
            }}
            disabled={disableWithoutCartScan} 
            settings={settings} 
            skey="picking_without_cart_scan"
              explainer={true}
              explainerTitle={t("core_pickbasis_without_cart_scan_title")}
              explainerText={t("core_pickbasis_without_cart_scan_text")}/>
            <BoolSetting 
            manualSave
            onChange={handleChange}
            settings={settings} 
            skey="picking_can_pick_from_bulk"/>
            <BoolSetting 
            manualSave
            onChange={handleChange}
            settings={settings} 
            skey="picking_never_pick_from_bulk"/>
            <BoolSetting 
            manualSave
            onChange={handleChange}
            settings={settings} 
            skey="picking_walkingroute"/>
            <BoolSetting 
            manualSave
            onChange={handleChange}
            settings={settings} 
            skey="picking_snakepicking"/>
            {/* <BoolSetting 
              settings={settings} 
              skey="picking_without_count_confirm"
            />
            <BoolSetting 
              settings={settings} 
              skey="picking_without_product_scan"
            /> */}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PickBasis;
